import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";
import { Menu } from "../types/user";

export interface Role {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RoleCreateRequest {
  name: string;
  description: string;
  isActive?: boolean;
  assignedBy?: number;
}

export interface RoleUpdateRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  assignedBy?: number;
}

export interface RoleMenuPermission {
  id?: number;
  roleId: number;
  menuId: number;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  isActive: boolean;
  assignedAt?: string;
  assignedBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface RoleWithPermissions extends Role {
  menuPermissions: RoleMenuPermission[];
}

// New interface for the actual API response structure
export interface RoleWithPermissionsResponse {
  role: Role;
  menuPermissions: RoleMenuPermission[];
}

export interface RoleCreateWithPermissionsRequest extends RoleCreateRequest {
  menuPermissions: RoleMenuPermission[];
}

export interface RoleUpdateWithPermissionsRequest extends RoleUpdateRequest {
  menuPermissions: RoleMenuPermission[];
}

class RoleApiService extends BaseApiService {

  // Get all roles
  async getRoles(): Promise<ApiResponse<Role[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role[]>>(API_ENDPOINTS.ROLES.LIST);
      return response;
    } catch (error) {
      toast.error('Failed to fetch roles');
      throw error;
    }
  }

  // Get role by ID
  async getRoleById(id: string): Promise<ApiResponse<Role>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role>>(API_ENDPOINTS.ROLES.DETAIL(id));
      return response;
    } catch (error) {
      toast.error('Failed to fetch role details');
      throw error;
    }
  }

  // Get role with menu permissions
  async getRoleWithPermissions(id: string): Promise<ApiResponse<RoleWithPermissionsResponse>> {
    try {
      const response = await this.makeRequest<ApiResponse<RoleWithPermissionsResponse>>(API_ENDPOINTS.ROLES.WITH_PERMISSIONS(id));
      return response;
    } catch (error) {
      toast.error('Failed to fetch role permissions');
      throw error;
    }
  }

  // Get all menus
  async getMenus(activeOnly: boolean = true): Promise<ApiResponse<Menu[]>> {
    try {
      const params = new URLSearchParams({
        activeOnly: activeOnly.toString()
      });
      const response = await this.makeRequest<ApiResponse<Menu[]>>(`/auth/menus?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to fetch menus');
      throw error;
    }
  }

  // Create new role
  async createRole(roleData: RoleCreateRequest): Promise<ApiResponse<Role>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role>>(API_ENDPOINTS.ROLES.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(roleData),
      });
      toast.success('Role created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create role');
      throw error;
    }
  }

  // Create role with permissions
  async createRoleWithPermissions(roleData: RoleCreateWithPermissionsRequest): Promise<ApiResponse<Role>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role>>(`${API_ENDPOINTS.ROLES.CREATE}/with-permissions`, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(roleData),
      });
      toast.success('Role created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create role');
      throw error;
    }
  }

  // Update role
  async updateRole(id: string, roleData: RoleUpdateRequest): Promise<ApiResponse<Role>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role>>(API_ENDPOINTS.ROLES.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(roleData),
      });
      toast.success('Role updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update role');
      throw error;
    }
  }

  // Update role with permissions
  async updateRoleWithPermissions(id: string, roleData: RoleUpdateWithPermissionsRequest): Promise<ApiResponse<Role>> {
    try {
      // console.log('🔧 API Call: Updating role with permissions at:', API_ENDPOINTS.ROLES.WITH_PERMISSIONS(id));
      // console.log('🔧 HTTP Method:', HTTP_METHODS.PUT);
      // console.log('🔧 Request Data:', roleData);
      
      const response = await this.makeRequest<ApiResponse<Role>>(API_ENDPOINTS.ROLES.WITH_PERMISSIONS(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(roleData),
      });
      toast.success('Role updated successfully!');
      return response;
    } catch (error) {
      // console.error('🔧 API Error:', error);
      toast.error('Failed to update role');
      throw error;
    }
  }

  // Delete role
  async deleteRole(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(API_ENDPOINTS.ROLES.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Role deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete role');
      throw error;
    }
  }

  // Search roles
  async searchRoles(query: string, page: number = 1, pageSize: number = 10): Promise<ApiResponse<Role[]>> {
    try {
      const params = new URLSearchParams({
        keyword: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Role[]>>(`${API_ENDPOINTS.ROLES.SEARCH}?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to search roles');
      throw error;
    }
  }
}

export const roleApiService = new RoleApiService();
